<template>
    <!--
      화면이 바뀐 것을 스크린리더에 알리는 자리.

      단일 페이지 앱이라 라우트가 바뀌어도 문서가 새로 실리지 않는다. 제목(document.title)은
      라우트마다 다르게 넣고 있었지만, 제목이 바뀌는 것만으로는 NVDA 가 읽어 주지 않는다 -
      초점도 그대로 남아 있어서, 사용자는 방금 무엇이 열렸는지 알 수 없었다.
      그래서 화면 이름을 여기에 적어 넣어 소리로 알린다.
    -->
    <span class="visually-hidden" role="status" aria-live="polite">{{ routeAnnouncement }}</span>
    <component :is="layout" ref="layout"/>
</template>

<script>
import {defineAsyncComponent} from 'vue'
import store from "@/store"
import { $session } from "@/plugin/dot-framework-vue-1.0.8"

/**
 * 폰트 설정
 */
import '@/asset/css/font.css';
import FontFaceObserver from 'fontfaceobserver';
import "@/asset/css/global.scss"
import { toHtmlLang } from "@/util/htmlLang.mjs";
import { getRouteTitle } from "@/router";

/**
 * 레이아웃 설정
 */
// apps
const DefaultLayout = defineAsyncComponent(() => import("@/layout/apps/DefaultLayout.vue"))
const CloudLayout = defineAsyncComponent(() => import("@/layout/apps/CloudLayout.vue"))

// easy
const EasyCloudLayout = defineAsyncComponent(() => import("@/layout/easy/EasyCloudLayout.vue"))

// common
const WideLayout = defineAsyncComponent(() => import("@/layout/WideLayout.vue"))
const BlankLayout = defineAsyncComponent(() => import("@/layout/BlankLayout.vue"))
const EmbeddedBookLayout = defineAsyncComponent(() => import("@/layout/EmbeddedBookLayout.vue"))

export default {
    name: 'App',
    components: {
        DefaultLayout,
        BlankLayout,
        EmbeddedBookLayout,
        WideLayout,
        CloudLayout,
        EasyCloudLayout
    },
    provide() {
        return {
            dotFilter: this.$filter,
            dotMixins: this.$mixins
        }
    },
    data() {
        return {
            layout: null,
            convertCheckInterval: null,
            /** 화면이 바뀔 때 소리로 알릴 이름. 비어 있으면 아무 말도 하지 않는다(첫 로드). */
            routeAnnouncement: "",
        }
    },
    created() {
        this.setFont();
        this.setHTMLLangAttribute()
        this.initLocale()
    },
    beforeMount() {
        document.getElementById("spinner").classList.add('d-none');
    },
    watch: {
        $route(to, from) {
            if (to.meta.layout !== undefined) {
              this.layout = to.meta.layout
            } else {
              this.layout = "User"
            }

            // 같은 화면에서 주소의 query 만 맞춘 것(캔버스가 연 파일을 주소에 적는다, NEW-G6-2)은 화면이 바뀐 것이 아니다.
            // 다시 읽으면 저장·열기 알림 위에 화면 이름이 겹쳐 들린다.
            const queryOnly = to.meta.announceQueryChange === false && from?.name === to.name && from?.path === to.path
            if (!queryOnly) {
                // 레이아웃이 통째로 갈리는 순간에 같이 넣으면 묻힌다. 한 틱 뒤에 적어야 읽어 준다.
                this.routeAnnouncement = ""
                this.$nextTick(() => {
                    // 창 제목과 같은 이름(화면 이름은 화면 언어로)을 읽는다
                    this.routeAnnouncement = (to.meta.titleKey || to.meta.title !== undefined) ? getRouteTitle(to) : "Dot Apps"
                })
            }

            if (this.$store.getters["auth/isAuthenticated"]) {
                if (this.convertCheckInterval) {
                    clearInterval(this.convertCheckInterval);
                }
                this.convertCheckInterval = setInterval(() => {
                    if (this.checkPageVisibility()) {
                      this.loadAndShowPopups();
                    }
                }, 30000);
            } else {
                if (this.convertCheckInterval) {
                    clearInterval(this.convertCheckInterval);
                    this.convertCheckInterval = null;
                }
            }
        },
        '$store.getters["auth/isAuthenticated"]'(isAuthenticated) {
            if (!isAuthenticated) {
                if (this.convertCheckInterval) {
                    clearInterval(this.convertCheckInterval);
                    this.convertCheckInterval = null;
                }
            }
        },
        '$i18n.locale'() {
            this.setHTMLLangAttribute();
            // this.setHTMLDirAttribute();
        },
    },
    beforeDestroy() {
        // 컴포넌트가 파괴되기 전에 인터벌 클리어
        if (this.convertCheckInterval) {
            clearInterval(this.convertCheckInterval)
        }
    },
    methods: {
        setHTMLDirAttribute() {
            document.documentElement.setAttribute('dir', this.$i18n.locale === 'ar' ? 'rtl' : 'auto')
        },
        /**
         * 다국어 설정
         */
        setHTMLLangAttribute() {
            document.documentElement.setAttribute('lang', toHtmlLang(this.$i18n.locale))
        },
        initLocale() {
            const defaultLanguage = "en"

            // 1) 쿠키에 저장된 사용자 선택 언어가 있으면 우선 적용
            const cookieLocale = this.$cookies.get("user_locale")
            if (cookieLocale && this.$i18n.availableLocales.includes(cookieLocale)) {
                return this.$i18n.locale = cookieLocale
            }

            // 2) 쿠키가 없으면 브라우저 언어 (백엔드 IP 기반 결정값이 별도 설정한 경우는 그 값을 쿠키에 심어주는 것을 가정)
            const lang = navigator.language || navigator.userLanguage
            const code = lang.substring(0, 2)
            const availableLocales = this.$i18n.availableLocales.includes(code)

            return this.$i18n.locale = availableLocales ? code : defaultLanguage
        },
        /**
         * 폰트 설정
         * @see https://fonts.google.com/knowledge/glossary/fout
         */
        setFont() {
            const font = new FontFaceObserver('Helvetica Neue')

            font.load().then(function () {
                document.body.classList.add("fonts-loaded");
            }).catch(function () {
                console.log('Helvetica Neue failed to load.');
            })
        },

        async loadAndShowPopups() {
            try {
                const popupList = await store.dispatch("textbook/getConversionCompletePopup", $session.getUserNo())
                let message = ""

                if (popupList && popupList.length > 0) {
                    for (const popup of popupList) {
                        await store.dispatch("textbook/popupDisplayComplete", popup.DTM_NO)
                        if (popup.STATUS === "FAILED") {
                            message = this.$t('HTML 변환에 실패하였습니다.')
                        } else {
                            message = this.$t('HTML 변환이 완료되었습니다.')
                        }
                    }

                    this.showPopup(message);
                }
            } catch (error) {
                console.error("Failed to load and show popups:", error)
            }
        },
        showPopup(message) {
            const Toast = this.$swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            });

            Toast.fire({
                html: `<swalCustomTag style="color: black;"><b>${message}</b></swalCustomTag>`,
            });
        },
        checkPageVisibility() {
            return document.visibilityState === "visible"
        }
    }
}
</script>

<style scoped>
</style>
